import React, { useState, useEffect, useContext, useCallback } from "react";
import { API_URL } from "../components/api";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
} from "@mui/x-data-grid";
import "./Clients.css";
import Formo from "./Editform";
import ExcelSVG from "../../ExcelSVG.svg";
import FilterSVG from "../../FilterSVG.svg";
import * as XLSX from "xlsx";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import Typography from "@mui/material/Typography";
import Modal from "react-bootstrap/Modal";
import AuthContext from "../context/AuthContext";
import LanguageContext from "../context/LanguageContext";
import axios from "axios";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";

const AR_LETTER = /[\u0621-\u064A]/;

const normalizeAr = (value) =>
  String(value == null ? "" : value)
    .replace(/[\u064B-\u0652\u0640]/g, "")
    .replace(/[\u0622\u0623\u0625]/g, "\u0627")
    .replace(/\u0624/g, "\u0648")
    .replace(/[\u0626\u0649]/g, "\u064A")
    .replace(/\u0629/g, "\u0647")
    .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .toLowerCase()
    .trim();

const hasArabicName = (row) => AR_LETTER.test(String(row.fullname_ar || ""));

const AR_SEARCH_FIELDS = [
  "fullname_ar",
  "phone_number",
  "village_name_ar",
  "address_string",
];

function ClientTable() {
  const [data, setData] = useState([]);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const handleShow = () => setShow(true);
  const handleClose = () => setShow(false);

  const [searchQuery, setSearchQuery] = useState("");
  const { user } = useContext(AuthContext);
  const { language, t } = useContext(LanguageContext);
  let authTokens = JSON.parse(localStorage.getItem("authTokens"));
  const [filteredRowsData, setFilteredRowsData] = useState([]);
  const [searchQueryNoMiddleName, setSearchQueryNoMiddleName] = useState(""); // New search state
  const [filteredRowsNoMiddleName, setFilteredRowsNoMiddleName] = useState([]); // Filtered rows for no middle name

  const [filters, setFilters] = useState({
    full_name_en: "",
    phone_number: "",
    village_name: "",
    category: "",
    address_string: "",
  });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [gridFilterModel, setGridFilterModel] = useState({
    items: [],
  });

  const [searchInput, setSearchInput] = useState(""); // To capture search input

  // NEW: State to track column visibility
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [searchFilterTimeout, setSearchFilterTimeout] = useState(null);

  const [ClientDetails, setClientDetails] = useState({
    client_id: 0,
    info_id: 0,
    balance_id: 0,
    balance: 0,
    address_id: "",
    first_name: 0,
    middle_name: 0,
    last_name: "",
    phone_number: "",
    address_string: "",
    village_name: "",
    remote: "",
    office: "",
    potential: "",
    freeCollection: "",
    category: "",
    employee_en: "",
  });

  const updateClientDetails = (details) => {
    setClientDetails(details);
  };

  const fetchData = useCallback(async () => {
    try {
      const authTokens = JSON.parse(localStorage.getItem("authTokens"));
      const response = await axios.get(`${API_URL}/administration/clients/`, {
        headers: {
          Authorization: `Bearer ${authTokens.access}`,
        },
        params: {
          username: user.username,
          ...(language === "ar" ? { lang: "ar" } : {}),
        },
      });

      if (response.status === 200) {
        setData(response.data);
        console.log(response.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [user.username, language]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setFilters({
      full_name_en: "",
      phone_number: "",
      village_name: "",
      category: "",
      address_string: "",
    });
    setGridFilterModel({ items: [] });
    setSearchInput("");
    setSearchQuery("");
    setSearchQueryNoMiddleName("");
    sessionStorage.removeItem("searchClient");
    sessionStorage.removeItem("searchClientNoMiddleName");
  }, [language]);

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setSuccessMessage("");
    }, 3000);

    return () => window.clearTimeout(timeout);
  }, [successMessage]);

  const handleClientUpdated = (updatedFreeCollection, rowEdits = {}) => {
    const updateFreeCollection = (client) =>
      client.client_id === ClientDetails.client_id
        ? {
            ...client,
            ...rowEdits,
            free_collection: updatedFreeCollection,
          }
        : client;

    setClientDetails((current) => ({
      ...current,
      freeCollection: updatedFreeCollection,
    }));
    setData((current) =>
      current.map(updateFreeCollection)
    );
    setFilteredRowsData((current) =>
      current.map(updateFreeCollection)
    );
    setFilteredRowsNoMiddleName((current) =>
      current.map(updateFreeCollection)
    );

    handleClose();
    setSuccessMessage(t("clientsPage.clientUpdated"));
    fetchData();
  };

  const isAr = language === "ar";

  const columns = [
    {
      field: isAr ? "fullname_ar" : "fullname_en",
      headerName: t("clientsPage.columns.fullName"),
      flex: 2,
      minWidth: 250,
      renderCell: (params) => (
        <span
          className={
            (isAr ? params.row.has_middle_name_ar : params.row.has_middle_name)
              ? "client-name client-name-highlight"
              : "client-name client-name-default"
          }
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "phone_number",
      headerName: t("clientsPage.columns.phoneNumber"),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        // Check if phone number length is 7, then add '0' at the beginning
        let phone = params.row.phone_number;
        if (phone && phone.length === 7) {
          phone = "0" + phone; // Concatenate '0' to the phone number
        }
        return <span>{phone}</span>;
      },
    },
    {
      field: isAr ? "village_name_ar" : "village_name",
      headerName: t("clientsPage.columns.villageName"),
      flex: 1,
      minWidth: 150,
    },
    {
      field: "category",
      headerName: t("clientsPage.columns.category"),
      flex: 1,
      minWidth: 150,
      ...(isAr
        ? {
            valueGetter: (params) =>
              t("clientsPage.categories." + (params.row.category || "-")),
          }
        : {}),
    },
    {
      field: "address_string",
      headerName: t("clientsPage.columns.address"),
      flex: 1,
      minWidth: 250,
    },
    {
      field: "actions",
      headerName: t("clientsPage.columns.actions"),
      width: 70,
      renderCell: (params) => (
        <div>
          <button
            className="edit-client"
            onClick={() => {
              const details = {
                client_id: params.row.client_id,
                address_id: params.row.address_id,
                balance_id: params.row.balance_id,
                balance: params.row.balance_usd,
                info_id: params.row.info_id,
                first_name: params.row.first_name,
                middle_name: params.row.middle_name,
                last_name: params.row.last_name,
                phone_number: params.row.phone_number,
                address_string: params.row.address_string,
                village_name: params.row.village_name,
                remote: params.row.temp_collection,
                office: params.row.temp_collection_office,
                potential: params.row.potential,
                freeCollection: params.row.free_collection,
                village_id: params.row.village_id,
                employee_id: params.row.employee_id,
                first_name_ar: params.row.first_name_ar,
                middle_name_ar: params.row.middle_name_ar,
                last_name_ar: params.row.last_name_ar,
                category: params.row.category,
                employee_en: params.row.employee_en,
              };
              updateClientDetails(details);
              handleShow();
            }}
          >
            {t("clientsPage.edit")}
          </button>
        </div>
      ),
    },
  ];

  const getRowId = (row) => row.client_id;

  // Create a delayed search filter function
  const delaySearchFilter = (input) => {
    setSearchQuery(input);
  };

  useEffect(() => {
    const searchValue = sessionStorage.getItem("searchClient");
    Object.keys(sessionStorage).forEach((key) => {
      if (key !== "searchClient") {
        sessionStorage.removeItem(key);
      }
    });
    if (searchValue) {
      sessionStorage.setItem("searchClient", searchValue);
      setSearchQuery(searchValue);
    }
  }, []);

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchInput(query);
    sessionStorage.setItem("searchClient", query);

    if (searchFilterTimeout) {
      clearTimeout(searchFilterTimeout);
    }

    const newTimeout = setTimeout(() => {
      delaySearchFilter(query);
    }, 100);
    setSearchFilterTimeout(newTimeout);
  };

  const arRows = isAr ? data.filter(hasArabicName) : data;

  const matchesSearch = (row, query) => {
    if (isAr) {
      return normalizeAr(query)
        .split(" ")
        .filter((word) => word !== "")
        .every((word) =>
          AR_SEARCH_FIELDS.some((field) =>
            normalizeAr(row[field]).includes(word)
          )
        );
    }
    return query
      .split(" ")
      .every((word) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(word.toLowerCase().trim())
        )
      );
  };

  // IMPROVED: Combined filtering logic that works for both display AND export
  const filteredRows = arRows.filter((row) => {
    // Apply search query filter
    const searchMatch = matchesSearch(row, searchQuery);

    // Apply modal filters
    const modalFilterMatch = Object.keys(filters).every((key) => {
      if (!filters[key]) return true;

      if (key === "connectivity_dependent") {
        const filterValue = filters[key].toLowerCase();
        if (row.connectivity === "Fiber") {
          return (row.code || "").toLowerCase().includes(filterValue);
        } else if (row.connectivity === "Wireless") {
          return (row.name || "").toLowerCase().includes(filterValue);
        }
        return false;
      }

      const rowValue = String(row[key] || "").toLowerCase();
      const filterValue = String(filters[key]).toLowerCase();
      return rowValue.includes(filterValue);
    });

    // Apply DataGrid filters
    const gridFilterMatch = gridFilterModel.items.every((filterItem) => {
      if (!filterItem.value) return true;

      const cellValue = row[filterItem.field];
      const filterValue = filterItem.value;

      switch (filterItem.operator) {
        case "contains":
          return String(cellValue || "")
            .toLowerCase()
            .includes(String(filterValue).toLowerCase());
        case "equals":
          return String(cellValue || "") === String(filterValue);
        case "startsWith":
          return String(cellValue || "")
            .toLowerCase()
            .startsWith(String(filterValue).toLowerCase());
        case "endsWith":
          return String(cellValue || "")
            .toLowerCase()
            .endsWith(String(filterValue).toLowerCase());
        default:
          return true;
      }
    });

    return searchMatch && modalFilterMatch && gridFilterMatch;
  });

  const handleFilterChange = (field, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const clientsWithoutMiddleName = isAr
    ? arRows.filter((client) => !client.middle_name_ar)
    : data.filter((client) => !client.middle_name);

  const columnHeaders = isAr
    ? {
        fullname_ar: t("clientsPage.columns.fullName"),
        phone_number: t("clientsPage.columns.phoneNumber"),
        village_name_ar: t("clientsPage.columns.villageName"),
        category: t("clientsPage.columns.category"),
        address_string: t("clientsPage.columns.address"),
      }
    : {
        fullname_en: "Full Name",
        phone_number: "Phone Number",
        village_name: "Village Name",
        category: "Category",
        address_string: "Address",
      };

  // Format date fields properly for export — e.g. "21/02/2025 10:25:52 AM"
  const formatDateForExport = (dateValue) => {
    if (!dateValue) return "";
    const date = new Date(dateValue);

    // Format as DD/MM/YYYY HH:MM:SS AM/PM
    const formattedDate = date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    // Ensure consistent spacing before AM/PM
    return formattedDate.replace(" am", " AM").replace(" pm", " PM");
  };

  /// Corrected: Transform rows respecting column visibility
  const transformRowsForExport = (rows) => {
    return rows.map((row) => {
      const transformedRow = {};

      Object.keys(columnHeaders).forEach((field) => {
        // Only export if column is visible
        if (columnVisibilityModel[field] !== false) {
          const headerName = columnHeaders[field];

          // Default: use the value or empty string
          transformedRow[headerName] =
            isAr && field === "category"
              ? t("clientsPage.categories." + (row.category || "-"))
              : row[field] ?? "";
        }
      });

      return transformedRow;
    });
  };

  // IMPROVED: Export function that uses the already-filtered rows
  const handleExportToExcel = () => {
    console.log("Exporting data...");
    console.log("Total data rows:", data.length);
    console.log("Filtered rows:", filteredRows.length);
    console.log("Active filters:", {
      search: searchQuery,
      modalFilters: filters,
      gridFilters: gridFilterModel.items,
    });
    console.log("Column visibility:", columnVisibilityModel);

    // Always use filteredRows which has ALL filters applied
    const transformedRows = transformRowsForExport(filteredRows);

    console.log("Rows to export:", transformedRows.length);

    const worksheet = XLSX.utils.json_to_sheet(transformedRows);
    const exportHeaders = Object.keys(transformedRows[0] || {});
    worksheet["!cols"] = exportHeaders.map((header) => {
      const widest = transformedRows.reduce(
        (max, row) => Math.max(max, String(row[header] ?? "").length),
        header.length
      );
      return { wch: Math.min(Math.max(widest + 2, 12), 60) };
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      t("clientsPage.excelSheet")
    );
    XLSX.writeFile(workbook, t("clientsPage.excelFileName"));
  };

  const [isPressed, setIsPressed] = useState(false);

  const handleExportClick = () => {
    setIsPressed(true);
    handleExportToExcel();
    setTimeout(() => {
      setIsPressed(false);
    }, 200);
  };

  // Handle search for "Clients Without Middle Name"
  const handleSearchNoMiddleName = (event) => {
    const query = event.target.value;
    setSearchQueryNoMiddleName(query);
    sessionStorage.setItem("searchClientNoMiddleName", query); // Save search query to session
  };

  // UseEffect to persist search session for "Clients Without Middle Name"
  useEffect(() => {
    const searchValueNoMiddleName = sessionStorage.getItem(
      "searchClientNoMiddleName"
    );
    if (searchValueNoMiddleName) {
      setSearchQueryNoMiddleName(searchValueNoMiddleName);
    }
  }, []);

  const filteredRowsNoMiddleNames = clientsWithoutMiddleName.filter((row) =>
    matchesSearch(row, searchQueryNoMiddleName)
  );
  const clientsLocaleText = isAr
    ? {
        toolbarColumns: t("clientsPage.grid.toolbarColumns"),
        toolbarFilters: t("clientsPage.grid.toolbarFilters"),
        toolbarExport: t("clientsPage.grid.toolbarExport"),
        toolbarExportCSV: t("clientsPage.grid.toolbarExportCSV"),
        toolbarExportPrint: t("clientsPage.grid.toolbarExportPrint"),
        columnsPanelTextFieldLabel: t("clientsPage.grid.columnsPanelTextFieldLabel"),
        columnsPanelTextFieldPlaceholder: t("clientsPage.grid.columnsPanelTextFieldPlaceholder"),
        columnsPanelDragIconLabel: t("clientsPage.grid.columnsPanelDragIconLabel"),
        columnsPanelShowAllButton: t("clientsPage.grid.columnsPanelShowAllButton"),
        columnsPanelHideAllButton: t("clientsPage.grid.columnsPanelHideAllButton"),
        filterPanelAddFilter: t("clientsPage.grid.filterPanelAddFilter"),
        filterPanelDeleteIconLabel: t("clientsPage.grid.filterPanelDeleteIconLabel"),
        filterPanelOperators: t("clientsPage.grid.filterPanelOperators"),
        filterPanelOperatorAnd: t("clientsPage.grid.filterPanelOperatorAnd"),
        filterPanelOperatorOr: t("clientsPage.grid.filterPanelOperatorOr"),
        filterPanelColumns: t("clientsPage.grid.filterPanelColumns"),
        filterPanelInputLabel: t("clientsPage.grid.filterPanelInputLabel"),
        filterPanelInputPlaceholder: t("clientsPage.grid.filterPanelInputPlaceholder"),
        filterOperatorContains: t("clientsPage.grid.filterOperatorContains"),
        filterOperatorEquals: t("clientsPage.grid.filterOperatorEquals"),
        filterOperatorStartsWith: t("clientsPage.grid.filterOperatorStartsWith"),
        filterOperatorEndsWith: t("clientsPage.grid.filterOperatorEndsWith"),
        filterOperatorIsEmpty: t("clientsPage.grid.filterOperatorIsEmpty"),
        filterOperatorIsNotEmpty: t("clientsPage.grid.filterOperatorIsNotEmpty"),
        filterOperatorIsAnyOf: t("clientsPage.grid.filterOperatorIsAnyOf"),
        columnMenuLabel: t("clientsPage.grid.columnMenuLabel"),
        columnMenuShowColumns: t("clientsPage.grid.columnMenuShowColumns"),
        columnMenuFilter: t("clientsPage.grid.columnMenuFilter"),
        columnMenuHideColumn: t("clientsPage.grid.columnMenuHideColumn"),
        columnMenuUnsort: t("clientsPage.grid.columnMenuUnsort"),
        columnMenuSortAsc: t("clientsPage.grid.columnMenuSortAsc"),
        columnMenuSortDesc: t("clientsPage.grid.columnMenuSortDesc"),
        noRowsLabel: t("clientsPage.grid.noRowsLabel"),
        noResultsOverlayLabel: t("clientsPage.grid.noResultsOverlayLabel"),
        footerTotalRows: t("clientsPage.grid.footerTotalRows"),
        MuiTablePagination: {
          labelRowsPerPage: t("clientsPage.grid.rowsPerPage"),
          labelDisplayedRows: ({ from, to, count }) =>
            t("clientsPage.grid.pageRange", {
              from,
              to,
              count: count !== -1 ? count : ">" + to,
            }),
        },
      }
    : undefined;

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <Button
        onClick={() => setShowFilterModal(true)}
        variant="outline-primary"
        style={{
          border: "none",
        }}
      >
        <img
          src={FilterSVG}
          alt="Filter Icon"
          style={{
            color: "white",
            width: "18px",
            marginRight: "8px",
            filter: "invert(0)", // keep black icon
          }}
        />
        {t("clientsPage.filter")}
      </Button>
      {/*<GridToolbarFilterButton />*/}
      <GridToolbarExport />
      <Button
        onClick={handleExportClick}
        variant="outline-success"
        style={{
          display: "flex",
          alignItems: "center",
          padding: "5px 10px",
          fontSize: "14px",
          border: "none",
          backgroundColor: isPressed ? "white" : "transparent",
          color: isPressed ? "black" : "#007bff",
          cursor: "pointer",
        }}
      >
        <img
          src={ExcelSVG}
          alt="Excel Icon"
          style={{
            width: "20px",
            marginRight: "8px",
            filter: isPressed ? "invert(0)" : "invert(1)",
          }}
        />
        {t("clientsPage.excel")}
      </Button>
    </GridToolbarContainer>
  );

  return (
    <Tabs
      defaultActiveKey="allClients"
      id="client-tabs"
      className="mb-3 d-flex align-items-center"
    >
      <Tab eventKey="allClients" title={t("clientsPage.allClients")}>
        {successMessage && (
          <Alert
            variant="success"
            onClose={() => setSuccessMessage("")}
            dismissible
          >
            {successMessage}
          </Alert>
        )}
        <div className="ClientTable">
          {/* Content for All Clients */}
          <style>{`
        .MuiDataGrid-panelWrapper {
          background: #122632 !important;
        }
      `}</style>
          <input
            type="text"
            placeholder={t("clientsPage.search")}
            value={searchInput}
            onChange={handleSearchChange}
            className="search-client"
          />
          {!loading && data.length > 0 && (
            <div style={{ height: 520, width: "100%" }}>
              <DataGrid
                key={language}
                rows={filteredRows}
                columns={columns}
                getRowId={getRowId}
                filterModel={gridFilterModel}
                onFilterModelChange={(newModel) => setGridFilterModel(newModel)}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel) =>
                  setColumnVisibilityModel(newModel)
                }
                components={{
                  Toolbar: CustomToolbar,
                }}
                rowsPerPageOptions={[10, 100, 1000]}
                pagination
                localeText={clientsLocaleText}
                disableSelectionOnClick
              />
            </div>
          )}
        </div>
      </Tab>

      <Tab
        eventKey="clientsWithoutMiddleName"
        title={t("clientsPage.withoutMiddleName")}
      >
        <div className="ClientTable">
          <input
            type="text"
            placeholder={t("clientsPage.searchWithoutMiddleName")}
            value={searchQueryNoMiddleName}
            onChange={handleSearchNoMiddleName}
            className="search-client"
          />
          {!loading && filteredRowsNoMiddleNames.length > 0 && (
            <div style={{ height: 520, width: "100%", color: "white" }}>
              <DataGrid
                key={language}
                rows={filteredRowsNoMiddleNames}
                columns={columns}
                getRowId={getRowId}
                filterModel={gridFilterModel}
                onFilterModelChange={(newModel) => setGridFilterModel(newModel)}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel) =>
                  setColumnVisibilityModel(newModel)
                }
                sx={{
                  ".MuiDataGrid-cell span": {
                    color: "white !important",
                  },
                }}
                components={{
                  Toolbar: CustomToolbar,
                }}
                rowsPerPageOptions={[10, 100, 1000]}
                pagination
                localeText={clientsLocaleText}
                disableSelectionOnClick
              />
            </div>
          )}
          <Modal
            show={showFilterModal}
            onHide={() => setShowFilterModal(false)}
          >
            <Modal.Header closeButton>
              <Modal.Title>{t("clientsPage.filtersTitle")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {columns
                .filter((col) => col.field !== "actions")
                .map((col) => (
                  <div key={col.field} className="filter">
                    <label>{col.headerName}</label>
                    <input
                      type="text"
                      placeholder={t("clientsPage.filterBy", {
                        column: col.headerName,
                      })}
                      value={filters[col.field] || ""}
                      onChange={(e) =>
                        handleFilterChange(col.field, e.target.value)
                      }
                      className="filter-input"
                    />
                  </div>
                ))}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowFilterModal(false)}
              >
                {t("clientsPage.close")}
              </Button>
            </Modal.Footer>
          </Modal>
          <Modal show={show} onHide={handleClose} className="modal-lg">
            <Modal.Header closeButton>
              <Modal.Title>{t("clientsPage.editClient")}</Modal.Title>
            </Modal.Header>
            <div className="forma">
              <Formo
                key={ClientDetails.client_id}
                sub={ClientDetails.client_id}
                add={ClientDetails.address_id}
                inf={ClientDetails.info_id}
                fir={ClientDetails.first_name}
                mid={ClientDetails.middle_name}
                las={ClientDetails.last_name}
                addr={ClientDetails.address_string}
                pho={ClientDetails.phone_number}
                vill={ClientDetails.village_name}
                cat={ClientDetails.category}
                balance_id={ClientDetails.balance_id}
                balance={ClientDetails.balance}
                employee_id={ClientDetails.employee_en}
                remote={ClientDetails.remote}
                office={ClientDetails.office}
                potential={ClientDetails.potential}
                freeCollection={ClientDetails.freeCollection}
                village_id={ClientDetails.village_id}
                collector_id={ClientDetails.employee_id}
                first_name_ar={ClientDetails.first_name_ar}
                middle_name_ar={ClientDetails.middle_name_ar}
                last_name_ar={ClientDetails.last_name_ar}
                onSuccess={handleClientUpdated}
              />
            </div>
          </Modal>
        </div>
      </Tab>
    </Tabs>
  );
}

export default ClientTable;
